import Phaser from 'phaser';
import { GameMode } from './GameMode.js';

export class SquadMode extends GameMode {
  constructor() {
    super('Squad Battle', 'Small team battles (2v2, 3v3, 4v4, 5v5)');
    this.teams = ['red', 'blue'];
    this.teamColors = {
      'red': '#ff4444',
      'blue': '#4444ff'
    };
    this.squadSize = 3; // Default 3v3, can be adjusted
  }

  initialize(scene) {
    console.log('[GameMode] Initialized Squad Battle mode');
  }

  spawnPlayers(scene, players) {
    // Limit to squad size * 2 players
    const maxPlayers = this.squadSize * 2;
    const selectedPlayers = players.slice(0, maxPlayers);
    
    // Assign to two teams
    selectedPlayers.forEach((player, index) => {
      const teamId = index < this.squadSize ? 'red' : 'blue';
      const teamColor = this.teamColors[teamId];
      player.setTeam(teamId, teamColor);
    });

    // Spawn teams on opposite sides
    const worldBounds = scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    const margin = 200;
    
    // Red team (left side)
    const redPlayers = selectedPlayers.filter(p => p.team === 'red');
    redPlayers.forEach(player => {
      const x = Phaser.Math.Between(worldBounds.x + margin, worldBounds.x + worldBounds.width / 2 - margin);
      const y = Phaser.Math.Between(worldBounds.y + margin, worldBounds.y + worldBounds.height - margin);
      
      player.sprite.x = x;
      player.sprite.y = y;
      player.targetX = x;
      player.targetY = y;
    });
    
    // Blue team (right side)
    const bluePlayers = selectedPlayers.filter(p => p.team === 'blue');
    bluePlayers.forEach(player => {
      const x = Phaser.Math.Between(worldBounds.x + worldBounds.width / 2 + margin, worldBounds.x + worldBounds.width - margin);
      const y = Phaser.Math.Between(worldBounds.y + margin, worldBounds.y + worldBounds.height - margin);
      
      player.sprite.x = x;
      player.sprite.y = y;
      player.targetX = x;
      player.targetY = y;
    });
    
    console.log(`[GameMode] Spawned ${selectedPlayers.length} players in ${this.squadSize}v${this.squadSize} Squad Battle`);
  }

  checkCombat(scene) {
    // Team-aware combat (same as Team vs Team)
    scene.players.forEach(p => { p.prevBattling = p.isBattling; p.isBattling = false; });
    
    for (let i = 0; i < scene.players.length; i++) {
      for (let j = i + 1; j < scene.players.length; j++) {
        const p1 = scene.players[i];
        const p2 = scene.players[j];
        if (p1.isAlive && p2.isAlive && p1.canAttack(p2)) {
          const dist = Phaser.Math.Distance.Between(p1.sprite.x, p1.sprite.y, p2.sprite.x, p2.sprite.y);
          if (dist < scene.meleeRange) {
            p1.isBattling = true;
            p2.isBattling = true;
            // Combat!
            const now = scene.time.now;
            const damage = p1.calculateMeleeDamage();
            if (now > p1.lastAttackTime + scene.getAttackCooldownMs(p1)) {
              p1.lastAttackTime = now;
              if (typeof p1.applyAttackStaminaCost === 'function') {
                p1.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p1.isCharging) p1.isCharging = false;
              const willDie = p2.health <= damage;
              const actualDamage = p2.takeDamage(damage, { critical: !!p1.lastAttackWasCritical });
              p1.damageDealt += actualDamage;
              if (typeof p1.handleDamageDealtForUltimate === 'function') {
                p1.handleDamageDealtForUltimate(actualDamage);
              }
              scene.createAttackEffect(p1, p2);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p1);
                scene.sfxManager.onHit(p2, actualDamage, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p1, 'attack');
                scene.tauntManager.triggerTaunt(p1, 'hit');
                scene.tauntManager.triggerTaunt(p2, 'hurt');
              }
              if (!p2.isAlive) {
                p1.addElimination();
                scene.createDeathMarker(p2.sprite.x, p2.sprite.y, { popScale: p2.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p2);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p1, 'killed');
                  scene.tauntManager.triggerTaunt(p2, 'death');
                }
              }
            }

            if (!p1.isAlive || !p2.isAlive) {
              continue;
            }
            if (now > p2.lastAttackTime + scene.getAttackCooldownMs(p2)) {
              p2.lastAttackTime = now;
              if (typeof p2.applyAttackStaminaCost === 'function') {
                p2.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p2.isCharging) p2.isCharging = false;
              const damage2 = p2.calculateMeleeDamage();
              const willDie = p1.health <= damage2;
              const actualDamage2 = p1.takeDamage(damage2, { critical: !!p2.lastAttackWasCritical });
              p2.damageDealt += actualDamage2;
              if (typeof p2.handleDamageDealtForUltimate === 'function') {
                p2.handleDamageDealtForUltimate(actualDamage2);
              }
              scene.createAttackEffect(p2, p1);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p2);
                scene.sfxManager.onHit(p1, actualDamage2, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p2, 'attack');
                scene.tauntManager.triggerTaunt(p2, 'hit');
                scene.tauntManager.triggerTaunt(p1, 'hurt');
              }
              if (!p1.isAlive) {
                p2.addElimination();
                scene.createDeathMarker(p1.sprite.x, p1.sprite.y, { popScale: p1.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p1);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p2, 'killed');
                  scene.tauntManager.triggerTaunt(p1, 'death');
                }
              }
            }
          }
        }
      }
    }

    // Post-combat breathing
    if (scene.sfxManager) {
      scene.players.forEach(p => {
        if (p.prevBattling && !p.isBattling && p.isAlive) {
          scene.sfxManager.onBreath(p);
        }
      });
    }
  }

  checkWinCondition(scene) {
    const alivePlayers = scene.players.filter(p => p.isAlive);
    
    if (alivePlayers.length === 0 && scene.gameState === 'playing') {
      console.log('Squad match ended with no winner - all players died');
      return { winner: null, type: 'draw' };
    }
    
    // Check if only one team remains
    const teamsAlive = new Set();
    alivePlayers.forEach(player => {
      if (player.team) {
        teamsAlive.add(player.team);
      }
    });
    
    if (teamsAlive.size === 1 && scene.gameState === 'playing') {
      const winningTeam = Array.from(teamsAlive)[0];
      const teamPlayers = alivePlayers.filter(p => p.team === winningTeam);
      console.log(`Squad ${winningTeam} team wins with ${teamPlayers.length} survivors!`);
      return { winner: teamPlayers, type: 'team', team: winningTeam };
    }
    
    return null; // No winner yet
  }

  getPlayerTarget(player, allPlayers) {
    // Find nearest enemy (non-teammate)
    let nearestEnemy = null;
    let nearestDistance = Infinity;
    
    allPlayers.forEach(otherPlayer => {
      if (otherPlayer !== player && otherPlayer.isAlive && player.canAttack(otherPlayer)) {
        const dist = Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, otherPlayer.sprite.x, otherPlayer.sprite.y);
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestEnemy = otherPlayer;
        }
      }
    });
    
    return nearestEnemy;
  }
}
